import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Reads the device's connectivity flag. `navigator.onLine === false` is a
 * definitive "no network" signal from the OS/WebView; `true` means a network
 * interface exists. We deliberately treat only `false` as offline.
 */
function readOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

/**
 * Tracks whether the device currently has a network connection.
 *
 * IMPORTANT: the offline screen is driven ONLY by real device connectivity
 * (`navigator.onLine` + the window `online`/`offline` events fired by the
 * Android WebView). A failed HTTP request is NOT treated as "offline" on its
 * own — a request can fail for many reasons while the device is perfectly
 * online (server down, wrong backend URL, CORS, timeout). Those should surface
 * as normal error toasts, not the full-screen offline fallback.
 *
 * The `network-error` event (dispatched by the axios interceptors) is only
 * used as a secondary confirmation and acts *exclusively* when the device
 * itself already reports being offline.
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(readOnline);
  const [isChecking, setIsChecking] = useState(false);
  const timerRef = useRef(null);

  const recheck = useCallback(() => {
    setIsChecking(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    // navigator.onLine is synchronous; the short delay is purely so the
    // "Checking…" state is visible as feedback when the user taps retry.
    timerRef.current = setTimeout(() => {
      setIsOnline(readOnline());
      setIsChecking(false);
    }, 600);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleNetworkError = () => {
      // Only trust a request failure as "offline" when the device also
      // reports no connectivity — never flip to offline just because a
      // single request failed while we're actually online.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setIsOnline(false);
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("network-error", handleNetworkError);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("network-error", handleNetworkError);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { isOnline, isChecking, recheck };
}

export default useNetworkStatus;
